"use client"

import Box from "@mui/material/Box"
import Container from "@mui/material/Container"
import Typography from "@mui/material/Typography"
import { useEffect, useMemo, useState } from "react"
import { usePathname, useSearchParams } from "next/navigation"
import { useTranslation } from "react-i18next"
import Breadcrumbs from "@src/components/Breadcrumbs"
import { AppContainer, SectionContainer } from "@src/components"
import useRouterClient from "@src/hooks/use-router-client"
import { ROUTES } from "@src/constants"
import TabCategory from "./tab-category"
import ProductListSkeleton from "./product-list-skeleton"
import ProductSortBar from "./sort-bar"

type Props = {
  categories: any[]
  children: React.ReactNode
}

/** Shared product listing chrome (title/breadcrumbs/tabs). Children = product grid only. */
export default function ProductListingShell({ categories = [], children }: Props) {
  const { t } = useTranslation()
  const { router } = useRouterClient()
  const pathname = usePathname()
  const urlSearchParams = useSearchParams()
  const searchKey = urlSearchParams?.toString() || ""

  // Immediate skeleton on tab/filter click — loading.tsx only swaps after RSC starts.
  const [isNavigating, setIsNavigating] = useState(false)
  const [optimisticCategoryId, setOptimisticCategoryId] = useState<number | null>(
    null
  )
  const [optimisticSubSlugs, setOptimisticSubSlugs] = useState<string[] | null>(
    null
  )

  useEffect(() => {
    setIsNavigating(false)
    setOptimisticCategoryId(null)
    setOptimisticSubSlugs(null)
  }, [pathname, searchKey])

  const categorySlug = useMemo(() => {
    const match = pathname?.match(/\/product\/category\/([^/?#]+)/)
    return match?.[1] || null
  }, [pathname])

  const currentCategory = useMemo(
    () => categories.find(item => item.slug === categorySlug) || null,
    [categories, categorySlug]
  )

  const currentCategoryId = currentCategory?.id ?? -1
  const currentCategorySlug = currentCategory?.slug || ""

  const currentSubCategorySlug = useMemo(() => {
    const keyword = urlSearchParams?.get("keyword")
    if (!keyword) return ["all"]
    return keyword.split(",").filter(Boolean)
  }, [urlSearchParams])

  const displayCategoryId = optimisticCategoryId ?? currentCategoryId
  const displaySubCategorySlug = optimisticSubSlugs ?? currentSubCategorySlug

  const breadcrumbs = useMemo(() => {
    const items = [
      { name: t("home"), link: ROUTES.HOME_PAGE },
      { name: t("product"), link: ROUTES.PRODUCT }
    ]
    const crumbCategory =
      (optimisticCategoryId != null
        ? categories.find(item => item.id === optimisticCategoryId)
        : null) || currentCategory
    if (crumbCategory?.name) {
      items.push({
        name: crumbCategory.name,
        link: `${ROUTES.PRODUCT_CATEGORY}/${crumbCategory.slug}`
      })
    }
    return items
  }, [t, currentCategory, optimisticCategoryId, categories])

  const beginNavigate = () => setIsNavigating(true)

  const onSelectCategory = catSlug => {
    if (catSlug === "all" || !catSlug) {
      if (!categorySlug) return
      beginNavigate()
      setOptimisticCategoryId(-1)
      setOptimisticSubSlugs(["all"])
      router.push(ROUTES.PRODUCT)
      return
    }
    if (catSlug === categorySlug && !urlSearchParams?.get("keyword")) return
    beginNavigate()
    const cat = categories.find(item => item.slug === catSlug)
    setOptimisticCategoryId(cat?.id ?? -1)
    setOptimisticSubSlugs(["all"])
    router.push(`${ROUTES.PRODUCT_CATEGORY}/${catSlug}`)
  }

  const onSelectSubCategory = ({ slug }) => {
    beginNavigate()
    if (slug === "all") {
      setOptimisticSubSlugs(["all"])
      router.push(`${ROUTES.PRODUCT_CATEGORY}/${currentCategorySlug}`)
      return
    }
    const live = new URLSearchParams(urlSearchParams?.toString() || "")
    const selected = currentSubCategorySlug || ["all"]
    let nextSlugs: string[]
    if (selected.includes(slug)) {
      const next = selected.filter(item => item !== slug)
      nextSlugs = next.length > 0 ? next : ["all"]
      if (next.length > 0) live.set("keyword", next.join(","))
      else live.delete("keyword")
    } else {
      nextSlugs = [...selected, slug].filter(item => item !== "all")
      if (nextSlugs.length > 0) live.set("keyword", nextSlugs.join(","))
    }
    setOptimisticSubSlugs(nextSlugs)
    live.delete("page")
    const qs = live.toString()
    router.push(
      qs
        ? `?${qs}`
        : `${ROUTES.PRODUCT_CATEGORY}/${currentCategorySlug}`
    )
  }

  const onSelectCatAndSub = ({ catSlug, subCatSlug = [] }) => {
    beginNavigate()
    if (catSlug === "all" || !catSlug) {
      setOptimisticCategoryId(-1)
      setOptimisticSubSlugs(["all"])
      router.push(ROUTES.PRODUCT)
      return
    }
    const cat = categories.find(item => item.slug === catSlug)
    setOptimisticCategoryId(cat?.id ?? -1)
    if (subCatSlug.includes("all") || subCatSlug?.length === 0) {
      setOptimisticSubSlugs(["all"])
      router.push(`${ROUTES.PRODUCT_CATEGORY}/${catSlug}`)
      return
    }
    setOptimisticSubSlugs(subCatSlug)
    const live = new URLSearchParams()
    live.set("keyword", subCatSlug.join(","))
    router.push(`${ROUTES.PRODUCT_CATEGORY}/${catSlug}?${live.toString()}`)
  }

  const productGrid = isNavigating ? (
    <AppContainer maxWidth={true} sx={{ padding: "0 !important" }}>
      <SectionContainer
        maxWidth={true}
        sxWrapper={{ margin: 0 }}
        sx={{ flexDirection: "column", paddingTop: 0 }}
      >
        <Box mt={-3}>
          <ProductListSkeleton />
        </Box>
      </SectionContainer>
    </AppContainer>
  ) : (
    children
  )

  return (
    <Box component="main" sx={{ flexGrow: 1 }}>
      <Container disableGutters maxWidth={false} sx={{ margin: "auto" }}>
        <Container
          maxWidth="xl"
          sx={{
            padding: {
              xs: "16px 12px 0px",
              md: "24px 16px 0px"
            }
          }}
        >
          <Typography
            variant="h1"
            sx={{
              textTransform: "uppercase",
              fontSize: "48px",
              textAlign: "center",
              padding: "16px 0",
              display: {
                xs: "none",
                xl: (optimisticCategoryId ?? categorySlug) ? "none" : "block"
              }
            }}
          >
            {t("product")}
          </Typography>
          <Breadcrumbs items={breadcrumbs} />
        </Container>

        <AppContainer maxWidth={true} sx={{ padding: "0px !important" }}>
          <SectionContainer
            maxWidth={true}
            sxWrapper={{ margin: 0 }}
            sx={{
              flexDirection: "column",
              padding: {
                xs: "0px !important",
                md: "0px 24px !important"
              }
            }}
          >
            <Box mt={2}>
              <TabCategory
                currentCategoryId={displayCategoryId}
                currentSubCategorySlug={displaySubCategorySlug}
                categories={categories}
                onSelectCategory={onSelectCategory}
                onSelectSubCategory={onSelectSubCategory}
                onSelectCatAndSub={onSelectCatAndSub}
              />
            </Box>
          </SectionContainer>
        </AppContainer>

        <AppContainer maxWidth={true} sx={{ padding: "0 !important" }}>
          <SectionContainer
            maxWidth={true}
            sxWrapper={{ margin: 0 }}
            sx={{ flexDirection: "column", paddingTop: 0 }}
          >
            <ProductSortBar />
          </SectionContainer>
        </AppContainer>

        {productGrid}
      </Container>
    </Box>
  )
}
